import type { CypherDataType, CypherProperty } from '@nx/neo4j-sdk';
import type { CypherStringStyle, StringStyle } from '@nx/neo4j-sdk/src/data-transforms/record-to-string';
import { propertyToString } from '@nx/neo4j-sdk/src/data-transforms/record-to-string';
import { isCypherPropertyType, isCypherTemporalType, isInt8Array } from '@nx/neo4j-sdk/src/types/cypher-data-types';
import type { Node, Path, Relationship } from 'neo4j-driver';
import { isInt, isNode, isPath, isPoint, isRelationship } from 'neo4j-driver';
import React from 'react';

import { ClickableUrls } from '../graph-viz-sidepanel/clickable-urls';
import { useDarkMode } from '../hooks/use-dark-mode';

const lightColors = {
  string: 'text-[#718A33]',
  number: 'text-[#a37acc]',
  label: 'text-[#f07171]',
  relType: 'text-[#f07171]',
};

const darkColors = {
  string: 'text-[#D1E5A1]',
  number: 'text-[#dfbfff]',
  label: 'text-[#f28779]',
  relType: 'text-[#f28779]',
};

export function propertyToJSX(property: CypherProperty, darkMode: boolean) {
  const colors = darkMode ? darkColors : lightColors;
  if (Array.isArray(property)) {
    return (
      <>
        [
        {property.map((p, index) => (
          <React.Fragment key={`prop-array-${index}`}>
            {propertyToJSX(p, darkMode)}
            {index !== property.length - 1 && ', '}
          </React.Fragment>
        ))}
        ]
      </>
    );
  }

  if (typeof property === 'boolean') {
    return <span className={colors.number}>{propertyToString(property)}</span>;
  }

  if (isInt(property)) {
    return <span className={colors.number}>{propertyToString(property)}</span>;
  }

  if (typeof property === 'bigint') {
    return <span className={colors.number}>{propertyToString(property)}</span>;
  }

  if (typeof property === 'number') {
    return <span className={colors.number}>{propertyToString(property)}</span>;
  }
  if (typeof property === 'string') {
    return (
      <span className={colors.string}>
        <ClickableUrls text={propertyToString(property)} />
      </span>
    );
  }

  if (isInt8Array(property)) {
    return <span>{propertyToString(property)}</span>;
  }

  if (isCypherTemporalType(property)) {
    return <span className={colors.number}>{propertyToString(property)}</span>;
  }

  if (isPoint(property)) {
    return <span className={colors.number}>{propertyToString(property)}</span>;
  }

  // This case shouldn't be used, but added as a fallback
  return <span>{String(property)}</span>;
}

type RenderOptions = {
  stringStyle: CypherStringStyle;
  darkMode: boolean;
  quoteStrings: boolean;
};

const Indent = ({ length }: { length: number }): JSX.Element => {
  return <>{'\u00A0\u00A0'.repeat(length)}</>;
};

type SinglePathProps = {
  path: Path;
  options: RenderOptions;
  cypherShellStyleFn: (value: Node | Relationship | Path, options: RenderOptions, indentation: number) => JSX.Element;
};

const SinglePath = ({ path, options, cypherShellStyleFn }: SinglePathProps): JSX.Element => {
  return <React.Fragment key="cst-path-0">{cypherShellStyleFn(path.start, options, 0)}</React.Fragment>;
};

export const prettyCypherShellStyleJSX = (
  value: Node | Relationship | Path,
  options: RenderOptions,
  indentation: number,
) => {
  const { stringStyle, darkMode, quoteStrings } = options;
  const includeIds = stringStyle === 'cypher-with-ids';
  const colors = darkMode ? darkColors : lightColors;

  if (isNode(value)) {
    const labels = value.labels.length > 0 ? `:${value.labels.join(':')}` : '';
    const propertiesArray = Object.entries(value.properties).map(
      ([key, val]: [string, CypherProperty], index, array) => {
        return (
          <React.Fragment key={`cst-node-${index}`}>
            <span>{key}</span>: {propertyToJSX(val, darkMode)}
            {index !== array.length - 1 ? ', ' : ''}
            {index !== array.length - 1 && (
              <>
                <br />
                <Indent length={indentation + 1} />
              </>
            )}
          </React.Fragment>
        );
      },
    );

    if (includeIds) {
      propertiesArray.push(
        // We only push elementId since we are phasing out id
        <span>
          {'<elementId>:'}
          {quoteStrings ? `"${value.elementId.toString()}"` : value.elementId.toString()}
        </span>,
      );
    }

    const renderBraces = propertiesArray.length > 0;

    return (
      <>
        <Indent length={indentation} />(
        {labels && (
          <>
            <span className={colors.label}>{labels}</span>
          </>
        )}
        {renderBraces ? ' { ' : ''}
        {propertiesArray.length > 1 && (
          <>
            <br />
            <Indent length={indentation + 1} />
          </>
        )}
        {propertiesArray}
        {propertiesArray.length > 1 && (
          <>
            <br />
            <Indent length={indentation} />
          </>
        )}
        {renderBraces ? '}' : ''})
      </>
    );
  } else if (isRelationship(value)) {
    const type = value.type ? `:${value.type}` : '';
    const propertiesArray = Object.entries(value.properties).map(
      ([key, val]: [string, CypherProperty], index, array) => {
        return (
          <React.Fragment key={`cst-rel-${key}`}>
            <span>{key}</span>: {propertyToJSX(val, darkMode)}
            {index !== array.length - 1 ? ', ' : ''}
            {index !== array.length - 1 && (
              <>
                <br />
                <Indent length={indentation + 1} />
              </>
            )}
          </React.Fragment>
        );
      },
    );

    if (includeIds) {
      // We only push elementId since we are phasing out id
      propertiesArray.push(
        <span>
          {'<elementId>:'}
          {quoteStrings ? `"${value.elementId.toString()}"` : value.elementId.toString()}
        </span>,
      );
    }

    const space = type && propertiesArray.length > 0 ? ' ' : '';
    const renderBraces = propertiesArray.length > 0;

    return (
      <>
        <Indent length={indentation} />[
        {type && (
          <>
            <span className={colors.relType}>
              {type}
              {space}
            </span>
          </>
        )}
        {renderBraces ? '{ ' : ''}
        {propertiesArray.length > 1 && (
          <>
            <br />
            <Indent length={indentation + 1} />
          </>
        )}
        {propertiesArray}
        {renderBraces ? ' }' : ''}]
      </>
    );
  } else if (isPath(value)) {
    if (value.segments.length === 0 && value.start.elementId === value.end.elementId) {
      return <SinglePath path={value} options={options} cypherShellStyleFn={prettyCypherShellStyleJSX} />;
    }

    const segments = value.segments.map((segment, index) => {
      const results = [];
      if (index === 0) {
        results.push(prettyCypherShellStyleJSX(segment.start, options, indentation));
      }

      if (segment.start.elementId === segment.relationship.startNodeElementId) {
        results.push(
          <>
            <br />
            <br />-
          </>,
        );
        results.push(prettyCypherShellStyleJSX(segment.relationship, options, indentation));
        results.push(
          <>
            -{'>'} <br /> <br />
          </>,
        );
        results.push(prettyCypherShellStyleJSX(segment.end, options, indentation));
      } else {
        results.push(
          <>
            <br></br>
            <br></br>
            {'<'}-
          </>,
        );
        results.push(prettyCypherShellStyleJSX(segment.relationship, options, indentation));
        results.push(
          <>
            -<br></br>
            <br></br>
          </>,
        );
        results.push(prettyCypherShellStyleJSX(segment.end, options, indentation));
      }
      return results.map((item, idx) => <React.Fragment key={`cst-path-${idx}`}>{item}</React.Fragment>);
    });

    return <React.Fragment>{segments}</React.Fragment>;
  }

  return <></>;
};

export const cypherShellStyleJSX = (value: Node | Relationship | Path, options: RenderOptions, indentation: number) => {
  const { stringStyle, darkMode, quoteStrings } = options;
  const includeIds = stringStyle === 'cypher-with-ids';
  const colors = darkMode ? darkColors : lightColors;

  if (isNode(value)) {
    const labels = value.labels.length > 0 ? `:${value.labels.join(':')}` : '';
    const propertiesArray = Object.entries(value.properties).map(
      ([key, val]: [string, CypherProperty], index, array) => {
        return (
          <React.Fragment key={`cst-node-${index}`}>
            <span>{key}</span>: {propertyToJSX(val, darkMode)}
            {index !== array.length - 1 ? ', ' : ''}
          </React.Fragment>
        );
      },
    );

    if (includeIds) {
      propertiesArray.push(
        // We only push elementId since we are phasing out id
        <span>
          {'<elementId>:'}
          {quoteStrings ? `"${value.elementId.toString()}"` : value.elementId.toString()}
        </span>,
      );
    }
    const space = labels.length > 0 && propertiesArray.length > 0 ? ' ' : '';
    const renderBraces = propertiesArray.length > 0;
    return (
      <>
        (
        {labels && (
          <span className={colors.label}>
            {labels}
            {space}
          </span>
        )}
        {renderBraces ? '{' : ''}
        {propertiesArray}
        {renderBraces ? '}' : ''})
      </>
    );
  } else if (isRelationship(value)) {
    const type = value.type ? `:${value.type}` : '';
    const propertiesArray = Object.entries(value.properties).map(
      ([key, val]: [string, CypherProperty], index, array) => {
        return (
          <React.Fragment key={`cst-rel-${key}`}>
            <span>{key}</span>: {propertyToJSX(val, darkMode)}
            {index !== array.length - 1 ? ', ' : ''}
          </React.Fragment>
        );
      },
    );

    if (includeIds) {
      // We only push elementId since we are phasing out id
      propertiesArray.push(
        <span>
          {'<elementId>:'}
          {quoteStrings ? `"${value.elementId.toString()}"` : value.elementId.toString()}
        </span>,
      );
    }
    const space = type && propertiesArray.length > 0 ? ' ' : '';
    const renderBraces = propertiesArray.length > 0;
    return (
      <>
        [
        {type && (
          <span className={colors.relType}>
            {type}
            {space}
          </span>
        )}
        {renderBraces ? '{' : ''}
        {propertiesArray}
        {renderBraces ? '}' : ''}]
      </>
    );
  } else if (isPath(value)) {
    if (value.segments.length === 0 && value.start.elementId === value.end.elementId) {
      return <SinglePath path={value} options={options} cypherShellStyleFn={cypherShellStyleJSX} />;
    }

    const segments = value.segments.map((segment, index) => {
      const results = [];
      if (index === 0) {
        results.push(cypherShellStyleJSX(segment.start, options, indentation));
      }

      if (segment.start.elementId === segment.relationship.startNodeElementId) {
        results.push(<>-</>);
        results.push(cypherShellStyleJSX(segment.relationship, options, indentation));
        results.push(<>-{'>'}</>);
        results.push(cypherShellStyleJSX(segment.end, options, indentation));
      } else {
        results.push(<>{'<'}-</>);
        results.push(cypherShellStyleJSX(segment.relationship, options, indentation));
        results.push(<>-</>);
        results.push(cypherShellStyleJSX(segment.end, options, indentation));
      }
      return results.map((item, idx) => <React.Fragment key={`cst-path-${idx}`}>{item}</React.Fragment>);
    });

    return <React.Fragment>{segments}</React.Fragment>;
  }

  return <></>;
};

type RecursiveRenderProps = {
  value: CypherDataType;
  darkMode: boolean;
  usePrettyPrint: boolean;
  indentation: number;
  stringStyle: StringStyle;
};

const recursiveRender = ({
  value,
  darkMode,
  usePrettyPrint,
  indentation = 0,
  stringStyle = 'json',
}: RecursiveRenderProps): JSX.Element => {
  // Keeping these as non tailwind classes since tailwind needs to know what the classes are beforehand.
  // Otherwise we would need to specify a lot of classes
  // ref: https://www.codeconcisely.com/posts/tailwind-css-dynamic-class/
  const nextDepthStyle = { paddingLeft: `${indentation + 1}rem` };
  const nextDepthIndentation = usePrettyPrint ? indentation + 1 : indentation;

  if (isCypherPropertyType(value)) {
    return propertyToJSX(value, darkMode);
  }

  if (Array.isArray(value)) {
    return (
      <>
        {usePrettyPrint && <Indent length={indentation} />}[{usePrettyPrint && <br />}
        {value.map((v, index) => (
          <React.Fragment key={`rec-render-arr${index}`}>
            {recursiveRender({ value: v, darkMode, usePrettyPrint, indentation: indentation + 1, stringStyle })}
            {index !== value.length - 1 && ', '}
            {usePrettyPrint && index !== value.length - 1 && <br />}
          </React.Fragment>
        ))}
        {usePrettyPrint && <br />}
        {usePrettyPrint && <Indent length={indentation} />}]
      </>
    );
  }

  // Now we have nodes, relationships, paths, and cypher maps left.
  if (stringStyle !== 'json' && (isNode(value) || isRelationship(value) || isPath(value))) {
    const options = { stringStyle, darkMode, quoteStrings: true };
    return (
      <>
        {usePrettyPrint
          ? prettyCypherShellStyleJSX(value, options, indentation)
          : cypherShellStyleJSX(value, options, indentation)}
      </>
    );
  }

  const entries = Object.entries(value);

  if (entries.length === 0) {
    return <>{'{}'}</>;
  }

  return (
    <>
      {usePrettyPrint && indentation === 1 && <Indent length={indentation} />}
      {'{'}
      {entries.map(([key, val]: [string, CypherDataType], index) => (
        // Having this as a div is not ideal for perf but the indentation is wacky without it.
        // It might be possible to get it working without it but if we have conservative overscan
        // it should be fine.
        // (For reference, removing this div can bring down the amount of DOM elements
        //  when rendering complex JSON by ~40% depending on the data.)
        <div key={`jsx-table-map-${key}-${indentation}`} style={usePrettyPrint ? {} : nextDepthStyle}>
          {usePrettyPrint && <Indent length={indentation + 1} />}
          {key}:&nbsp;
          {recursiveRender({
            value: val,
            darkMode,
            usePrettyPrint,
            indentation: nextDepthIndentation,
            stringStyle,
          })}
          {index !== entries.length - 1 && ', '}
        </div>
      ))}
      <>
        {usePrettyPrint && <Indent length={indentation} />}
        {'}'}
      </>
    </>
  );
};

const complexityOfProperty = (value: CypherProperty): number => (Array.isArray(value) ? value.length : 1);

const cypherElementCount = (value: Node | Relationship | Path): number => {
  let count = 0;

  if (isNode(value)) {
    count += value.labels.length;
    count += Object.keys(value.properties).reduce((acc, key) => {
      const property: unknown = value.properties[key];
      if (isCypherPropertyType(property)) {
        return acc + complexityOfProperty(property);
      }
      return acc;
    }, 0);

    return count;
  } else if (isRelationship(value)) {
    const propertiesCount = Object.keys(value.properties).length;

    if (value.type) {
      count += 1;
    }

    count += propertiesCount;

    return count;
  } else if (isPath(value)) {
    value.segments.forEach((segment) => {
      count += cypherElementCount(segment.start);
      if (segment.start.elementId === segment.relationship.startNodeElementId) {
        count += 1;
        count += cypherElementCount(segment.relationship);
        count += 1;
        count += cypherElementCount(segment.end);
      } else {
        count += 1;
        count += cypherElementCount(segment.relationship);
        count += 1;
        count += cypherElementCount(segment.end);
      }
    });

    return count;
  }

  return 0;
};

export const countComplexity = (value: CypherDataType): number => {
  if (isCypherPropertyType(value)) {
    return complexityOfProperty(value);
  }
  if (isNode(value) || isRelationship(value) || isPath(value)) {
    return cypherElementCount(value);
  }
  const entries = Object.entries(value);
  if (entries.length === 0) {
    return 1;
  }
  return entries.reduce((acc, [_, val]: [string, CypherDataType]) => acc + countComplexity(val), 0);
};

export const CypherDataToJSX = ({
  map,
  stringStyle = 'json',
  usePrettyPrint,
}: {
  map: CypherDataType;
  stringStyle: StringStyle;
  usePrettyPrint: boolean;
}) => {
  const isDarkMode = useDarkMode();

  return recursiveRender({ value: map, darkMode: isDarkMode, usePrettyPrint, indentation: 0, stringStyle });
};
